import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

/** Ein Mitarbeiter, wie ihn die Einstellungen zeigen (A36). */
export type NutzerVerwaltung = {
  id: number;
  nc_username: string;
  display_name: string | null;
  /** Das SELBST GESETZTE Kürzel — null, solange niemand eines eingetragen hat. */
  kuerzel: string | null;
  /** Was die Oberfläche wirklich anzeigt: gesetzt oder aus dem Namen gerechnet. */
  kuerzel_effektiv: string;
  farbe: string | null;
  symbol: string | null;
  /** Arbeitet dieser Mensch noch hier? (A42) */
  aktiv: boolean;
  zuletzt_gesehen: string | null;
};

export type NutzerPools = {
  farben: string[];
  symbole: string[];
};

/**
 * Die Mitarbeiterliste samt Pools (A36).
 *
 * Die Pools kommen vom Server mit, statt hier ein zweites Mal zu stehen:
 * Vergabe (api/lib/auth.php) und Pflege müssen aus derselben Liste
 * schöpfen, sonst trägt jemand irgendwann eine Farbe, die die Vergabe
 * gar nicht kennt (Fallstrick 15).
 */
export function useNutzerVerwaltung() {
  return useQuery({
    queryKey: ['nutzer-verwaltung'],
    queryFn: () => apiFetch<{ nutzer: NutzerVerwaltung[]; pools: NutzerPools }>('/nutzer/liste.php'),
    staleTime: 60_000
  });
}

export type NutzerPatch = {
  id: number;
  display_name?: string;
  kuerzel?: string;
  farbe?: string;
  symbol?: string;
};

/**
 * Eine Änderung schicken.
 *
 * Danach werden ALLE Listen ungültig, die Mitarbeiter zeigen: die
 * Verwaltung selbst, die @-Vorschlagsliste und die eigene Anmeldung
 * (Farbe und Symbol stehen oben in der Kopfzeile). Wer nur die erste
 * erneuert, sieht seine neue Farbe erst nach dem nächsten Laden.
 */
export function useNutzerAendern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: NutzerPatch) =>
      apiFetch<{ geaendert: string[]; nutzer: NutzerVerwaltung }>('/nutzer/aendern.php', {
        method: 'PATCH',
        body: JSON.stringify(patch)
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['nutzer-verwaltung'] });
      void qc.invalidateQueries({ queryKey: ['nutzer'] });
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    }
  });
}

/**
 * Stilllegen oder wieder aktivieren (A42).
 *
 * Eigene Mutation und nicht ein Feld in useNutzerAendern(): `aktiv` ist
 * keine Angabe ÜBER einen Menschen wie Name oder Farbe, sondern eine
 * Aussage darüber, ob er noch da ist. Der Server hat dafür einen eigenen
 * Endpunkt mit eigener Ablehnung (sich selbst) und eigenem
 * Protokoll-Verb.
 *
 * Ungültig werden dieselben Listen wie beim Ändern — und aus demselben
 * Grund noch die Erfasser-Auswahl des Dashboards: Wer stillgelegt wird,
 * verschwindet dort, und wer das gerade getan hat, soll es sehen, ohne
 * neu zu laden.
 */
export function useNutzerAktivSetzen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (arg: { id: number; aktiv: boolean }) =>
      apiFetch<{ geaendert: boolean; nutzer: NutzerVerwaltung }>('/nutzer/aktiv.php', {
        method: 'POST',
        body: JSON.stringify(arg)
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['nutzer-verwaltung'] });
      void qc.invalidateQueries({ queryKey: ['nutzer'] });
      void qc.invalidateQueries({ queryKey: ['bestellungen'] });
    }
  });
}
